import { focusOnBurgerButton } from "../../../services/focus";
import * as router from "../../../services/routing-manager";
import animationManager from "../../../services/animation-manager";
import {
  AnimationSequences,
  AnimationSequencesType,
  FOCUSABLE_ELEMENTS_SELECTOR,
} from "@phn/utility/constants";
import {
  findTopLevelRoutingKey,
  RoutingKeys,
} from "@phn/entities/routing-key";
import { navStateStore } from "@phn/state/nav-state-store";
import { routerStore } from "@phn/state/router-store";
import {
  focusOnElement,
  getHTMLElement,
  getHTMLElementFromRoot,
  isString,
} from "@phn/utility/helper";

export const handleAnimationRouting = (): AnimationRouting => {
  const actions: Record<Operations, () => AnimationRouting> = {
    OPENING: openingAction,
    CLOSING: closingAction,
    FORWARD: forwardAction,
    BACKWARDS: backwardAction,
    RESIZED: resizedAction,
    CHANGE_SAME_LEVEL: sameLevelAction,
    UNCHANGED: unchangedAction,
  };

  const conditionToExecute = getDoubleDrawerAction();

  return actions[conditionToExecute]();
};

type Operations =
  | "OPENING"
  | "CLOSING"
  | "FORWARD"
  | "BACKWARDS"
  | "UNCHANGED"
  | "RESIZED"
  | "CHANGE_SAME_LEVEL";
export function getDoubleDrawerAction(): Operations {
  if (router.isResized()) {
    return "RESIZED";
  }
  if (router.isOpening()) {
    return "OPENING";
  }
  if (router.isClosing()) {
    return "CLOSING";
  }

  if (router.isAnimatingForward()) {
    return "FORWARD";
  }
  if (router.isAnimatingBackward()) {
    return "BACKWARDS";
  }

  if (router.isAnimatingSameLevel()) {
    return "CHANGE_SAME_LEVEL";
  }

  return "UNCHANGED";
}

interface AnimationRouting {
  route: string;
}

const openingAction = (): AnimationRouting => {
  setTimeout(() => {
    animationManager.play(AnimationSequences.OPEN_DOUBLE_DRAWER).then(() => {
      router.syncAnimation();
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
    });
  }, 200);

  return { route: router.getCurrentRoute() };
};

const closingAction = (): AnimationRouting => {
  animationManager.play(AnimationSequences.CLOSE_DOUBLE_DRAWER).then(() => {
    router.syncAnimation();
    if (navStateStore.state.usingKeyboard) {
      focusOnBurgerButton();
    }
  });

  return { route: router.getAnimatedRoute() };
};

const forwardAction = (): AnimationRouting => {
  const topLevelRoutingKey = findTopLevelRoutingKey(router.getCurrentRoute());
  if (topLevelRoutingKey === RoutingKeys.DEALER_SEARCH) {
    animationManager.play("transitionSameLevelDoubleDrawer").then(() => {
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
    });
  } else {
    animationManager.play("transitionNextLevelDoubleDrawer").then(() => {
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
    });
  }

  return { route: router.getAnimatedRoute() };
};
const backwardAction = (): AnimationRouting => {
  const topLevelRoutingKey = findTopLevelRoutingKey(router.getAnimatedRoute());

  if (topLevelRoutingKey === RoutingKeys.DEALER_SEARCH) {
    animationManager.play("transitionSameLevelDoubleDrawer").then(() => {
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
      router.syncAnimation();
      router.pop();
    });
  } else {
    animationManager.play("transitionPreviousLevelDoubleDrawer").then(() => {
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
      router.pop();
    });
  }

  return { route: router.getAnimatedRoute() };
};

const sameLevelAction = (): AnimationRouting => {
  animationManager.play("transitionSameLevelDoubleDrawer").then(() => {
    focusOnInitialElement();
    window.dispatchEvent(new CustomEvent("finishedAnimation"));
  });

  return { route: router.getAnimatedRoute() };
};

const unchangedAction = (): AnimationRouting => {
  return { route: router.getAnimatedRoute(true) };
};

const resizedAction = (): AnimationRouting => {
  setTimeout(() => {
    animationManager.play(AnimationSequences.OPEN_DOUBLE_DRAWER).then(() => {
      router.syncAnimation();
      focusOnInitialElement();
      window.dispatchEvent(new CustomEvent("finishedAnimation"));
    });
  }, 150);
  return { route: router.getAnimatedRoute(true) };
};

export const isPlayingDoubleDrawerAnimation = (): boolean => {
  const doubleDrawerAnimationSequences: AnimationSequencesType[] = [
    AnimationSequences.OPEN_DOUBLE_DRAWER,
    AnimationSequences.CLOSE_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_NEXT_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_PREVIOUS_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_SAME_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_SAME_LEVEL_DOUBLE_DRAWER_REVERSE,
  ];

  return doubleDrawerAnimationSequences.some((sequence) => {
    try {
      return animationManager.isPlaying(sequence);
    } catch {
      return false;
    }
  });
};

export function focusOnInitialElement(): void {
  const element = getHTMLElementFromRoot(".phn-double-drawer");

  if (!element) return;

  const isNavigatingBackwards =
    routerStore.state.routingKeyPointer <
    routerStore.state.routingKeyHistory.length - 1;

  let focusedRoutingKey = isNavigatingBackwards
    ? routerStore.state.routingKeyHistory[
        routerStore.state.routingKeyPointer + 1
      ]
    : router.getCurrentRoute();
  if (focusedRoutingKey.startsWith(RoutingKeys.DEALER_SEARCH)) {
    [focusedRoutingKey] = focusedRoutingKey.split("/");
  }

  const autoFocusElement = getHTMLElement(element.querySelector(".autofocus"));
  if (autoFocusElement !== null) {
    focusOnElement(autoFocusElement);
    return;
  }

  if (navStateStore.state.usingKeyboard) {
    let focusableElement: HTMLElement | null = null;

    if (isString(focusedRoutingKey)) {
      focusableElement = getHTMLElement(
        element.querySelector(`[data-id="${focusedRoutingKey}"]`),
      );
    }

    if (focusableElement === null) {
      focusableElement = getHTMLElement(
        element.querySelector(`${FOCUSABLE_ELEMENTS_SELECTOR}`),
      );
    }

    if (focusableElement !== null) {
      focusOnElement(focusableElement);
    }

    return;
  }

  const firstFocus = getHTMLElement(element.querySelector(".first-focus"));
  if (firstFocus !== null) {
    focusOnElement(firstFocus);
  }
}
